import { useEffect, useMemo, useRef, useState } from 'react'

import { IncidentsFilterDrawerMode } from '../../hooks/useDrawerMode'
import { useIncidentFilterFormValues } from '../../hooks/useIncidentFilterFormValues'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { Controller, UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'
import { Box, Button, FilterIcon, Flex, Spinner, Text } from '@hivewatch/dark'

import { MultiSelectFilterRow } from '@/components/filter-drawer'
import { DeviceType, IncidentSource } from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { selectActiveFilter } from '@/redux/incidents/incidentsSlice'
import { FilterOptionI } from '@/utils/filterDrawers'
import {
  FacilityOptionWithAddressI,
  getFacilityOptionsWithMatchedByAddressCue,
} from '@/utils/forms/optionBuilders'
import { getFacilityNoOptionsMessage } from '@/utils/forms/validation'

import { IncidentFiltersFormInputsI } from '../../types/form'
import { SavedFilterI } from '../../types/generic'
import { CreatedAtFilterRow } from './CreatedAtFilterRow'
import { DarkCreateFilterModal } from './DarkCreateFilterModal'
import { DarkUpdateFilterConfirmationModal } from './DarkUpdateFilterConfirmationModal'
import { RadioFilterRow } from './RadioFilterRow'
import { SelectFilterRow } from './SelectFilterRow'

interface DarkIncidentsFilterFormIProps {
  drawerMode: IncidentsFilterDrawerMode
  editedFilter: SavedFilterI | null
  onGoBack: () => void
  methods: UseFormMethods<IncidentFiltersFormInputsI>
  colorScheme: ColorScheme
}

export const DarkIncidentsFilterForm = ({
  drawerMode,
  editedFilter,
  onGoBack,
  methods,
  colorScheme,
}: DarkIncidentsFilterFormIProps) => {
  const { t } = useTranslation()
  const activeFilter = useAppSelector(selectActiveFilter)
  const isIncidentRulesEnabled = useFeatureFlag(FeatureFlag.incidentRules)

  const { control, watch, formState, reset } = methods
  const lastDefaultValuesKeyRef = useRef<string | null>(null)

  const {
    defaultValues,
    options,
    deviceSearchValue,
    onDeviceSearchChange,
    facilitySearchValue,
    onFacilitySearchChange,
    isDeviceLoading,
    isFloorLoading,
    isFacilityLoading,
    isFacilityError,
  } = useIncidentFilterFormValues({ watch, editedFilter })

  useEffect(() => {
    if (!defaultValues) return

    const defaultValuesKey = JSON.stringify(defaultValues)
    if (lastDefaultValuesKeyRef.current === defaultValuesKey) return

    reset(defaultValues)
    lastDefaultValuesKeyRef.current = defaultValuesKey
  }, [defaultValues, reset])

  const [isModalOpen, setIsModalOpen] = useState(false)

  const facilityNameOptions = useMemo(
    () =>
      options?.facilityNameOptions &&
      getFacilityOptionsWithMatchedByAddressCue(
        options.facilityNameOptions as FacilityOptionWithAddressI[],
        t('common:facility_matched_by_address')
      ),
    [options?.facilityNameOptions, t]
  )

  if (defaultValues === null || options === null) {
    return (
      <Flex justifyContent='center' py='40px'>
        <Spinner />
      </Flex>
    )
  }

  const secondaryTextColor =
    colorScheme === 'light' ? 'light.text.secondary' : 'text.secondary'
  const primaryTextColor =
    colorScheme === 'light' ? 'light.text.primary' : 'text.primary'

  return (
    <>
      <Flex
        alignItems='center'
        gap='12px'
        justifyContent='space-between'
        mb='16px'
      >
        {activeFilter ? (
          <Flex alignItems='center' gap='8px' minWidth='0'>
            <FilterIcon colorScheme={colorScheme} />
            <Box minWidth='0'>
              <Text
                color={primaryTextColor}
                overflow='hidden'
                textOverflow='ellipsis'
                textStyle='caption-medium'
                whiteSpace='nowrap'
              >
                {activeFilter.name}
              </Text>
              <Text color={secondaryTextColor} textStyle='small-body'>
                {t('incidents:filter_active_label')}
              </Text>
            </Box>
          </Flex>
        ) : (
          <Box />
        )}
        <Button
          colorScheme={colorScheme}
          disabled={editedFilter ? !formState.isDirty : false}
          onClick={() => setIsModalOpen(true)}
          variant='secondary'
        >
          {t('incidents:filter_save')}
        </Button>
      </Flex>
      <Flex direction='column' gap='16px'>
        <Controller
          control={control}
          defaultValue={defaultValues.facilityName}
          name='facilityName'
          render={({ onChange, value }) => (
            <MultiSelectFilterRow
              colorScheme={colorScheme}
              isLoading={isFacilityLoading}
              label={t('incidents:filter_facility_name')}
              noOptionsMessage={({ inputValue }) =>
                getFacilityNoOptionsMessage(
                  inputValue,
                  isFacilityError,
                  t('common:facility_search_error')
                )
              }
              onChange={onChange}
              onSearchChange={onFacilitySearchChange}
              options={facilityNameOptions}
              placeholder={t('incidents:filter_facility_placeholder')}
              preserveOptionOrder
              searchValue={facilitySearchValue}
              value={value}
            />
          )}
        />
        <Controller
          control={control}
          defaultValue={defaultValues.floorName}
          name='floorName'
          render={({ onChange, value }) => (
            <MultiSelectFilterRow
              colorScheme={colorScheme}
              isLoading={isFloorLoading}
              label={t('incidents:filter_floor_name')}
              onChange={onChange}
              options={options.floorNameOptions}
              placeholder={t('incidents:filter_floor_placeholder')}
              value={value}
            />
          )}
        />
        <Controller
          control={control}
          defaultValue={defaultValues.deviceName}
          name='deviceName'
          render={({ onChange, value }) => (
            <MultiSelectFilterRow
              colorScheme={colorScheme}
              isLoading={isDeviceLoading}
              label={t('incidents:filter_device_name')}
              onChange={onChange}
              onSearchChange={onDeviceSearchChange}
              options={options.deviceNameOptions}
              placeholder={
                onDeviceSearchChange === null
                  ? t('incidents:filter_device_placeholder')
                  : t('incidents:filter_device_search_placeholder')
              }
              searchValue={deviceSearchValue}
              value={value}
            />
          )}
        />
        <Controller
          control={control}
          defaultValue={defaultValues.createdAt}
          name='createdAt'
          render={({ onChange, value }) => (
            <CreatedAtFilterRow
              colorScheme={colorScheme}
              label={t('common:created_at')}
              onChange={onChange}
              options={options.createdAtOptions}
              placeholder={t('incidents:filter_created_at_placeholder')}
              value={value}
            />
          )}
        />
        <Controller
          control={control}
          defaultValue={defaultValues.incidentStatus}
          name='incidentStatus'
          render={({ onChange, value }) => (
            <MultiSelectFilterRow
              colorScheme={colorScheme}
              label={t('incidents:filter_incident_status')}
              onChange={onChange}
              options={options.incidentStatusOptions}
              placeholder={t('incidents:filter_incident_status_placeholder')}
              value={value}
            />
          )}
        />
        <Controller
          control={control}
          defaultValue={defaultValues.sourceType}
          name='sourceType'
          render={({ onChange, value }) => (
            <MultiSelectFilterRow
              colorScheme={colorScheme}
              label={t('incidents:filter_source_type')}
              onChange={onChange}
              options={
                options.sourceTypeOptions as FilterOptionI<
                  DeviceType | IncidentSource
                >[]
              }
              placeholder={t('incidents:filter_source_type_placeholder')}
              value={value}
            />
          )}
        />
        <Controller
          control={control}
          defaultValue={defaultValues.incidentType}
          name='incidentType'
          render={({ onChange, value }) => (
            <MultiSelectFilterRow
              colorScheme={colorScheme}
              label={t('incidents:filter_incident_type')}
              onChange={onChange}
              options={options.incidentTypeOptions}
              placeholder={t('incidents:filter_incident_type_placeholder')}
              value={value}
            />
          )}
        />
        <Controller
          control={control}
          defaultValue={defaultValues.priority}
          name='priority'
          render={({ onChange, value }) => (
            <MultiSelectFilterRow
              colorScheme={colorScheme}
              getOptionKey={(option) => option.label}
              label={t('incidents:filter_priority')}
              onChange={onChange}
              options={options.priorityOptions as FilterOptionI<unknown>[]}
              placeholder={t('incidents:filter_priority_placeholder')}
              value={value}
            />
          )}
        />
        {isIncidentRulesEnabled && (
          <Controller
            control={control}
            defaultValue={defaultValues.mutedStatus}
            name='mutedStatus'
            render={({ onChange, value }) => (
              <SelectFilterRow
                colorScheme={colorScheme}
                data-testid='incidentsPage_filterDrawer_mute'
                label={t('incidents:filter_rule_action')}
                onChange={onChange}
                options={options.mutedOptions}
                placeholder={t('incidents:filter_rule_action_placeholder')}
                value={value}
              />
            )}
          />
        )}
        <Controller
          control={control}
          defaultValue={defaultValues.clips}
          name='clips'
          render={({ onChange, value }) => (
            <SelectFilterRow
              colorScheme={colorScheme}
              data-testid='incidentsPage_filterDrawer_clips'
              label={t('incidents:filter_clips')}
              onChange={onChange}
              options={options.clipsOptions}
              placeholder={t('incidents:filter_clips_placeholder')}
              value={value}
            />
          )}
        />
        <Controller
          control={control}
          defaultValue={defaultValues.aiOperatorState}
          name='aiOperatorState'
          render={({ onChange, value }) => (
            <RadioFilterRow
              colorScheme={colorScheme}
              label={t('incidents:filter_ai_operator')}
              name='aiOperatorStateRadio'
              onChange={onChange}
              options={options.aiOperatorStateOptions}
              placeholder={t('incidents:filter_ai_operator_placeholder')}
              value={value}
            />
          )}
        />
      </Flex>
      {isModalOpen &&
        (drawerMode === IncidentsFilterDrawerMode.UPDATE && editedFilter ? (
          <DarkUpdateFilterConfirmationModal
            colorScheme={colorScheme}
            editedFilter={editedFilter}
            incidentFiltersFormValues={watch()}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false)
              onGoBack()
            }}
          />
        ) : (
          <DarkCreateFilterModal
            colorScheme={colorScheme}
            incidentFiltersFormValues={watch()}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        ))}
    </>
  )
}
